import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useState } from "react";
import { authentication } from "../firebase";
import { logAuthEvent, hashIdentifier } from "../utils/logAuthEvent";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/features/auth";
import { useForm } from "react-hook-form";
import { cn } from "@/utils/stylesUtils";

const ERROR_MESSAGES = {
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/user-not-found": "No account found with this email.",
  "auth/wrong-password": "Incorrect password. Please try again.",
  "auth/invalid-credential": "Incorrect email or password.",
  "auth/too-many-requests":
    "Too many attempts. Please wait a moment and try again.",
  "auth/user-disabled":
    "This account has been disabled. Please contact support.",
  default: "Sign-in failed. Please check your credentials and try again.",
};

function getFirebaseErrorMessage(code) {
  return ERROR_MESSAGES[code] ?? ERROR_MESSAGES.default;
}

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required.")
    .check(z.email("Please enter a valid email address.")),
  password: z.string().min(1, "Password is required."),
});

export default function EmailLogin() {
  const { firebaseLogin } = useAuth();
  const [resetNotice, setResetNotice] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    getValues,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function handleForgotPassword() {
    const email = (getValues("email") || "").trim().toLowerCase();
    if (!email || !z.email().safeParse(email).success) {
      setResetNotice("");
      setError("email", {
        message: "Enter your email above, then tap “Forgot password?”.",
      });
      return;
    }

    setResetSending(true);
    try {
      await sendPasswordResetEmail(authentication, email);
      logAuthEvent({
        message: "Password reset email requested",
        level: "info",
        capture: true,
        data: { emailHash: hashIdentifier(email) },
      });
    } catch (error) {
      // Don't reveal whether the account exists - log but show the same notice.
      logAuthEvent({
        message: `Password reset request failed: ${error?.code ?? "unknown"}`,
        error,
        level: "error",
        capture: true,
        data: { emailHash: hashIdentifier(email) },
      });
    } finally {
      setResetSending(false);
      setResetNotice(
        "If an account exists for that email, we've sent a link to reset your password.",
      );
    }
  }

  async function onSubmit({ email, password }) {
    const normalizedEmail = email.trim().toLowerCase();

    window.__loginStart = performance.now();
    window.__loginTimingTracked = false;

    try {
      const userCredential = await signInWithEmailAndPassword(
        authentication,
        normalizedEmail,
        password,
      );
      const idToken = await userCredential.user.getIdToken();
      await firebaseLogin(idToken);

      logAuthEvent({
        message: "Email login sucessful",
        level: "info",
        capture: true,
        data: { userId: userCredential.user.uid },
      });
    } catch (error) {
      const code = error?.code ?? "";

      logAuthEvent({
        message: `Email login failed: ${code || "unknown"}`,
        error,
        level: "error",
        capture: true,
        data: { emailHash: hashIdentifier(normalizedEmail) },
      });

      if (code.startsWith("auth/")) {
        setError("root", { message: getFirebaseErrorMessage(code) });
        return;
      }

      if (error?.response?.status === 401) {
        setError("root", {
          message:
            "Your account wasn't found in our system. Please contact your administrator.",
        });
        return;
      }

      setError("root", { message: "Something went wrong. Please try again" });
    }
  }

  return (
    <div id="outerEmailBackground">
      {errors.root && (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {errors.root.message}
        </div>
      )}
      <label className="labelText">Email</label>
      <input
        type="email"
        placeholder="you@example.com"
        autoComplete="email"
        disabled={isSubmitting}
        style={{ marginBottom: "10px" }}
        className="emailLoginInputs"
        {...register("email")}
      />
      {errors.email && (
        <p className="text-xs text-red-500">{errors.email.message}</p>
      )}

      <label className="labelText">Password</label>
      <input
        type="password"
        placeholder="••••••••"
        autoComplete="current-password"
        disabled={isSubmitting}
        style={{ marginBottom: "20px" }}
        className="emailLoginInputs"
        {...register("password")}
      />
      {errors.password && (
        <p className="text-xs text-red-500">{errors.password.message}</p>
      )}

      <div style={{ marginBottom: "12px", textAlign: "right" }}>
        <button
          type="button"
          onClick={handleForgotPassword}
          disabled={resetSending || isSubmitting}
          className="text-xs text-blue-600 underline disabled:opacity-50"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          {resetSending ? "Sending…" : "Forgot password?"}
        </button>
      </div>
      {resetNotice && (
        <p className="text-xs text-green-600" style={{ marginBottom: "12px" }}>
          {resetNotice}
        </p>
      )}

      <button
        id="signInButton"
        onClick={handleSubmit(onSubmit)}
        disabled={isSubmitting}
      >
        LOG IN
      </button>
    </div>
  );
}
